import React, { useState, useEffect } from 'react';
import {
  TextField,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  Chip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getAllRoles, addCustomRole } from '../utils/stakeholderService';

const RoleSelector = ({ 
  value, 
  onChange, 
  name, 
  label = "Role", 
  fullWidth = true, 
  margin = "dense",
  required = false 
}) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [addingRole, setAddingRole] = useState(false);
  const [error, setError] = useState('');

  // Load all roles on component mount
  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const allRoles = await getAllRoles();
      setRoles(allRoles);
    } catch (error) {
      console.error('Error loading roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (event, newValue) => {
    // Create a synthetic event object for consistency
    const syntheticEvent = {
      target: {
        name: name,
        value: newValue || ''
      }
    };
    onChange(syntheticEvent);
  };

  const handleAddNewRole = () => {
    setNewRoleName('');
    setError('');
    setOpenAddDialog(true);
  };

  const handleSaveNewRole = async () => {
    if (!newRoleName.trim()) {
      setError('Role name is required');
      return;
    }

    // Check if role already exists (case-insensitive)
    const existingRole = roles.find(role => 
      role.toLowerCase() === newRoleName.trim().toLowerCase()
    );
    
    if (existingRole) {
      setError('This role already exists');
      return;
    }

    setAddingRole(true);
    setError('');

    try {
      const result = await addCustomRole(newRoleName.trim());
      
      if (result.success) {
        // Reload roles to include the new one
        await loadRoles();
        
        // Set the new role as selected
        handleRoleChange(null, newRoleName.trim());
        
        // Close dialog
        setOpenAddDialog(false);
        setNewRoleName('');
      } else {
        setError(result.error || 'Failed to add role');
      }
    } catch (error) {
      setError('Error adding role. Please try again.');
    } finally {
      setAddingRole(false);
    }
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewRoleName('');
    setError('');
  };

  return (
    <>
      <Autocomplete
        value={value || ''}
        onChange={handleRoleChange}
        options={roles}
        loading={loading}
        freeSolo
        fullWidth={fullWidth}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            margin={margin}
            required={required}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {params.InputProps.endAdornment}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddNewRole}
                    sx={{ ml: 1, minWidth: 'auto' }}
                    variant="outlined"
                    color="primary"
                  >
                    Add
                  </Button>
                </Box>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Typography variant="body2">{option}</Typography>
            </Box>
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={option}
              {...getTagProps({ index })}
              key={option}
            />
          ))
        }
        filterOptions={(options, { inputValue }) => {
          const filtered = options.filter(option =>
            option.toLowerCase().includes(inputValue.toLowerCase())
          );
          
          // If the input doesn't match any existing option exactly, 
          // show option to add it as new
          const isExisting = options.some(option => 
            option.toLowerCase() === inputValue.toLowerCase()
          );
          
          if (inputValue !== '' && !isExisting) {
            filtered.push(`Add "${inputValue}"`);
          }
          
          return filtered;
        }}
        getOptionLabel={(option) => {
          if (typeof option === 'string' && option.startsWith('Add "')) {
            return option.slice(5, -1); // Remove 'Add "' and '"'
          }
          return option;
        }}
        isOptionEqualToValue={(option, value) => option === value}
      />

      {/* Add New Role Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Role</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            variant="outlined"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSaveNewRole();
              }
            }}
            placeholder="Enter the new role name"
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This role will be available for all future stakeholders.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={addingRole}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveNewRole} 
            variant="contained" 
            disabled={addingRole || !newRoleName.trim()}
          >
            {addingRole ? 'Adding...' : 'Add Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RoleSelector;